import axios from '../axios';

// ---- Google Places ----

export interface IPlaceSearchResult {
  placeId: string;
  name: string;
  address: string;
  city: string;
}

export interface IPlaceDetails {
  placeId: string;
  name: string;
  address: string;
  phone: string;
  internationalPhone: string;
  website: string;
  googleMapsUrl: string;
  rating: number;
  userRatingCount: number;
  businessStatus: string;
  primaryType: string;
  editorialSummary: string;
  openingHours: string[];
  types: string[];
  latitude: number;
  longitude: number;
}

export const searchPlaces = async (query: string): Promise<IPlaceSearchResult[]> => {
  const response = await axios.get(`/json/onboarding/places/search?query=${encodeURIComponent(query)}`);
  return response.data;
};

export const getPlaceDetails = async (placeId: string): Promise<IPlaceDetails> => {
  const response = await axios.get(`/json/onboarding/places/details?placeId=${encodeURIComponent(placeId)}`);
  return response.data;
};

// ---- Vertex AI Enrichment ----

export interface IEnrichmentData {
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  description?: string | null;
  socialLinks?: {
    facebook?: string | null;
    instagram?: string | null;
    linkedin?: string | null;
  };
  contactPerson?: string | null;
  businessId?: string | null;
  address?: string | null;
  city?: string | null;
  postCode?: string | null;
}

export interface IEnrichmentResult {
  data?: IEnrichmentData;
  rawText?: string;
  promptTokens?: number;
  candidateTokens?: number;
  totalTokens?: number;
  sources?: Array<{ title: string; url: string }>;
}

export const enrichCompany = async (data: {
  companyName: string;
  website?: string;
  city?: string;
}): Promise<IEnrichmentResult> => {
  const response = await axios.post('/json/onboarding/enrich', data);
  return response.data;
};

// ---- YTJ ----

export interface IYtjSearchResult {
  businessId: string;
  name: string;
  companyForm: string;
  registrationDate: string;
  isToiminimi: boolean;
  street?: string;
  postCode?: string;
  city?: string;
  businessLineCode?: string;
  businessLineName?: string;
  terminationDate?: string | null;
  isCeased?: boolean;
}

export interface IYtjSearchResponse {
  totalResults: number;
  companies: IYtjSearchResult[];
}

export interface IYtjDetails {
  businessId: string;
  name: string;
  companyForm: string;
  registrationDate: string;
  isToiminimi: boolean;
  phone?: string;
  website?: string;
  addresses?: Array<{
    type: number;
    street: string;
    postCode: string;
    city: string;
    country: string;
  }>;
  businessLines?: Array<{
    code: string;
    name: string;
  }>;
  hasLiquidations?: boolean;
  terminationDate?: string | null;
  isCeased?: boolean;
}

export const searchYtj = async (params: {
  name?: string;
  location?: string;
  mainBusinessLine?: string;
  companyForm?: string;
  postCode?: string;
  page?: number;
}): Promise<IYtjSearchResponse> => {
  const urlParams = new URLSearchParams();
  if (params.name) urlParams.append('name', params.name);
  if (params.location) urlParams.append('location', params.location);
  if (params.mainBusinessLine) urlParams.append('mainBusinessLine', params.mainBusinessLine);
  if (params.companyForm) urlParams.append('companyForm', params.companyForm);
  if (params.postCode) urlParams.append('postCode', params.postCode);
  if (params.page && params.page > 1) urlParams.append('page', params.page.toString());
  const response = await axios.get(`/json/onboarding/ytj/search?${urlParams.toString()}`);
  return response.data;
};

export const getYtjDetails = async (businessId: string): Promise<IYtjDetails> => {
  const response = await axios.get(`/json/onboarding/ytj/details?businessId=${encodeURIComponent(businessId)}`);
  return response.data;
};

// ---- Email Generation ----

export interface IGeneratedEmail {
  subject: string;
  body: string;
  error?: string;
  message?: string;
}

export const generateEmail = async (data: {
  companyName: string;
  contactName?: string;
  serviceToken?: string;
  companyForm?: string;
  city?: string;
  website?: string;
  businessType?: string;
}): Promise<IGeneratedEmail> => {
  const response = await axios.post('/json/onboarding/generate-email', data);
  return response.data;
};

// ---- Outreach Batch ----

export interface IOutreachBatch {
  id: string;
  created: number;
  updated: number;
  mainBusinessLine: string;
  mainBusinessLineName: string | null;
  postCode: string | null;
  city: string | null;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  totalFound: number;
  totalSaved: number;
  totalDuplicate: number;
  totalError: number;
  pages: number;
  errorMessage: string | null;
}

export const startOutreachBatch = async (data: {
  mainBusinessLine: string;
  mainBusinessLineName?: string;
  postCode?: string;
  city?: string;
}): Promise<IOutreachBatch> => {
  const response = await axios.post('/json/onboarding/start-outreach-batch', data);
  return response.data;
};

export const listOutreachBatches = async (): Promise<IOutreachBatch[]> => {
  const response = await axios.get('/json/onboarding/outreach-batches');
  return response.data;
};

export const getOutreachBatch = async (id: string): Promise<IOutreachBatch> => {
  const response = await axios.get(`/json/onboarding/outreach-batch?id=${id}`);
  return response.data;
};

export const getOutreachBatchLeads = async (batchId: string): Promise<any[]> => {
  const response = await axios.get(`/json/onboarding/outreach-batch/leads?batchId=${batchId}`);
  return response.data;
};

const OnboardingService = {
  searchPlaces,
  getPlaceDetails,
  enrichCompany,
  searchYtj,
  getYtjDetails,
  generateEmail,
  startOutreachBatch,
  listOutreachBatches,
  getOutreachBatch,
  getOutreachBatchLeads,
};

export default OnboardingService;

// ── Supply Pipeline & Knowledge Base Typings ─────────────────────────────

export interface ISupplyPipeline {
  id?: string;
  created?: number;
  updated?: number;
  operatorId?: string | null;
  serviceToken: string;
  serviceId?: string | null;
  regions?: string | null;
  tolCodes?: string | null;
  placesQueries?: string | null;
  evaluationCriteria?: string | null;
  status?: 'PENDING' | 'HARVESTING' | 'EVALUATING' | 'ENRICHING' | 'REVIEW' | 'COMPLETED';
  statHarvested?: number;
  statEvaluated?: number;
  statAccepted?: number;
  statMaybe?: number;
  statRejected?: number;
  statEnriched?: number;
  statPromoted?: number;
}

export interface ILead {
  id: string;
  created?: number;
  updated?: number;
  placeId?: string | null;
  placeName?: string | null;
  businessId?: string | null;
  ytjName?: string | null;
  companyForm?: string | null;
  businessLine?: string | null;
  street?: string | null;
  postCode?: string | null;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  contactPerson?: string | null;
  status: 'NOT_CONTACTED' | 'CONTACTED' | 'NOT_INTERESTED' | 'CONTRACT';
  operatorId?: string | null;
  isToiminimi?: boolean;
  gdprRestrictionColdEmail?: boolean;
  pipelineId?: string | null;
  serviceToken?: string | null;
  aiScore?: string | null;
  aiReason?: string | null;
  aiEvaluated?: boolean;
  enrichmentDone?: boolean;
  latitude?: number | null;
  longitude?: number | null;
  certifications?: string | null;
  linkedOrgId?: string | null;
}

export interface ILeadsResponse {
  leads: ILead[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface IServiceKnowledgeBase {
  id?: string;
  created?: number;
  updated?: number;
  serviceToken: string;
  serviceId?: string | null;
  verticalName?: string | null;
  marketResearchTaskId?: string | null;
  launchStatus?: string | null;

  serviceDescription?: string | null;
  typicalIncludesJson?: string | null;
  typicalExcludesJson?: string | null;
  deliveryType?: string | null;
  averageOrderValue?: number | null;
  priceRangeLow?: number | null;
  priceRangeHigh?: number | null;
  pricingModel?: string | null;
  seasonalityNotes?: string | null;

  tolCodesJson?: string | null;
  tolCodeNamesJson?: string | null;
  googlePlacesQueriesJson?: string | null;
  requiredCertificationsJson?: string | null;
  certificationBodiesJson?: string | null;
  evaluationCriteria?: string | null;
  excludeKeywordsJson?: string | null;
  estimatedProviderCount?: number | null;
  regionLimitationsJson?: string | null;

  legislativeBasis?: string | null;
  requiredPermitsJson?: string | null;
  requiredInsurance?: string | null;
  regulatoryBodiesJson?: string | null;
  taxDeductionEligible?: boolean | null;
  taxDeductionDetails?: string | null;
  taxDeductionSourceUrl?: string | null;
  hankixLiabilityNotes?: string | null;
  complianceSourceUrlsJson?: string | null;
  complianceCheckedAt?: number | null;

  targetGroupsJson?: string | null;
  primaryTriggersJson?: string | null;
  customerFearsJson?: string | null;
  buyingCriteria?: string | null;
  faqsJson?: string | null;
  kotitalousvahennysInfo?: string | null;
  tamEstimate?: number | null;
  samEstimate?: number | null;

  primaryKeyword?: string | null;
  secondaryKeywordsJson?: string | null;
  keywordClustersJson?: string | null;
  cityPageStrategy?: string | null;
  schemaJsonLd?: string | null;
  generatedArticlesJson?: string | null;

  siteConfigCode?: string | null;
  brandColor?: string | null;
  heroHeadline?: string | null;
  heroCopy?: string | null;
  infoGridBlocksJson?: string | null;
  promptFragment?: string | null;
}

// ── API Methods ──────────────────────────────────────────────────────────

export const startSupplyPipeline = async (data: {
  serviceToken: string;
  regions?: string;
  tolCodes?: string;
  placesQueries?: string;
  evaluationCriteria?: string;
}): Promise<ISupplyPipeline> => {
  const response = await axios.post('/json/supply-pipeline/start', data);
  return response.data;
};

export const listSupplyPipelines = async (operatorId?: string): Promise<ISupplyPipeline[]> => {
  const url = operatorId ? `/json/supply-pipeline/list?operatorId=${operatorId}` : '/json/supply-pipeline/list';
  const response = await axios.get(url);
  return response.data;
};

export const getSupplyPipelineStatus = async (pipelineId: string): Promise<ISupplyPipeline> => {
  const response = await axios.get(`/json/supply-pipeline/status?pipelineId=${pipelineId}`);
  return response.data;
};

export const getSupplyPipelineLeads = async (
  pipelineId: string,
  score?: string,
  page?: number
): Promise<ILeadsResponse> => {
  const urlParams = new URLSearchParams();
  urlParams.append('pipelineId', pipelineId);
  if (score) urlParams.append('score', score);
  if (page) urlParams.append('page', page.toString());
  
  const response = await axios.get(`/json/supply-pipeline/leads?${urlParams.toString()}`);
  return response.data;
};

export const approveLead = async (leadId: string): Promise<{ success: boolean; organizationId: string }> => {
  const response = await axios.post(`/json/supply-pipeline/approve?leadId=${leadId}`);
  return response.data;
};

export const rejectLead = async (leadId: string): Promise<{ success: boolean }> => {
  const response = await axios.post(`/json/supply-pipeline/reject?leadId=${leadId}`);
  return response.data;
};

export const reevaluateLead = async (leadId: string, customPrompt?: string): Promise<ILead> => {
  const response = await axios.post('/json/supply-pipeline/reevaluate', { leadId, customPrompt });
  return response.data;
};

export const getKnowledgeBase = async (serviceToken: string): Promise<IServiceKnowledgeBase> => {
  const response = await axios.get(`/json/knowledge-base?serviceToken=${encodeURIComponent(serviceToken)}`);
  return response.data;
};

export const updateKnowledgeBase = async (data: IServiceKnowledgeBase): Promise<IServiceKnowledgeBase> => {
  const response = await axios.put('/json/knowledge-base', data);
  return response.data;
};

export const listKnowledgeBases = async (): Promise<IServiceKnowledgeBase[]> => {
  const response = await axios.get('/json/knowledge-base/list');
  return response.data;
};

export const createKnowledgeBase = async (data: {
  serviceToken: string;
  verticalName: string;
  serviceId?: string | null;
}): Promise<IServiceKnowledgeBase> => {
  const response = await axios.post('/json/knowledge-base/create', data);
  return response.data;
};
